/**
 * antd's DatePicker/TimePicker use dayjs by default. Our codebase is still on
 * moment, so we generate moment-backed pickers via @rc-component/picker's
 * moment generator.
 *
 * Import these instead of `antd`'s DatePicker/TimePicker anywhere we rely on
 * moment semantics (Form onChange values, disabledDate callbacks, etc.).
 */
import type { PickerRef } from '@rc-component/picker';
import momentGenerateConfig from '@rc-component/picker/lib/generate/moment';
// Use the CommonJS build so jest can require it without ESM transforms.
import generatePicker from 'antd/lib/date-picker/generatePicker';
import type { Moment } from 'moment';
import React from 'react';

// Explicit return type avoids TS2742 caused by antd 6.4.x's nested @rc-component/picker.
const DatePicker: ReturnType<typeof generatePicker<Moment>> =
  generatePicker<Moment>(momentGenerateConfig);

// `generatePicker` returns a bare TimePicker function — shim a `.RangePicker`
// onto it (matches antd/es/time-picker/index.js) so callers can use `<TimePicker.RangePicker>`.
const TimeRangePicker = React.forwardRef<PickerRef, Record<string, unknown>>((props, ref) =>
  React.createElement(DatePicker.RangePicker, {
    ...props,
    picker: 'time',
    mode: undefined,
    ref,
  }),
);

type TimePickerWithRange = typeof DatePicker.TimePicker & {
  RangePicker: typeof TimeRangePicker;
};

const TimePicker = DatePicker.TimePicker as TimePickerWithRange;
TimePicker.RangePicker = TimeRangePicker;

export { DatePicker, TimePicker };
